import Divider from "@mui/material/Divider";
import HexagonOutlinedIcon from "@mui/icons-material/HexagonOutlined";

import { SpinnerCircularFixed } from "../../../../assets/spinner/SpinnerCircularFixed";
import SalesChannelOption from "./SalesChannelOption";
import { useSalesChannels } from "./useSalesChannels";
import {
    groupSalesChannelsByCategory,
    parsePublicationIds,
    serializePublicationIds,
    toggleChannel,
} from "./salesChannels";

type Props = {
    storeId: number;
    friendlyName: string;
    value: string;
    canEdit: boolean;
    onChange: (value: string) => void;
};

export default function SalesChannelsSection({
    storeId,
    friendlyName,
    value,
    canEdit,
    onChange,
}: Props) {
    const { channels, isLoading, error } = useSalesChannels(storeId, canEdit);

    const selectedIds = parsePublicationIds(value);

    if (!canEdit) return null;

    const updateSelection = (publicationIds: string[]) =>
        onChange(serializePublicationIds(publicationIds));

    const handleToggle = (publicationId: string) =>
        updateSelection(toggleChannel(selectedIds, channels, publicationId));

    const handleSelectAll = () =>
        updateSelection(channels.map((channel) => channel.publicationId));

    const handleClear = () => updateSelection([]);

    const groups = groupSalesChannelsByCategory(channels);
    const hasChannels = !isLoading && !error && channels.length > 0;
    const allSelected = hasChannels && channels.every((channel) => selectedIds.includes(channel.publicationId));

    return (
        <div className="sync-management salesChannel">
            <div className="sync-header">
                <p>Shopify Stores/{friendlyName}</p>
                <h1><HexagonOutlinedIcon />Shopify Auto Include Sales Channels</h1>
            </div>
            <Divider className="divider" />

            <div className="form-container">
                <div className="salesChannel__toolbar">
                    <p className="salesChannel__description">
                        Every product and collection will be published into the selected sales channels.
                    </p>

                    {hasChannels && (
                        <div className="salesChannel__actions">
                            <button type="button" className="salesChannel__action" onClick={handleSelectAll} disabled={allSelected}>
                                Select all
                            </button>
                            <button type="button" className="salesChannel__action" onClick={handleClear} disabled={selectedIds.length === 0}>
                                Clear
                            </button>
                        </div>
                    )}
                </div>

                {isLoading && (
                    <div className="spinner-div">
                        <SpinnerCircularFixed className="" size={35} thickness={140} speed={120} color="rgba(203, 216, 234, 1)" secondaryColor="rgba(0, 0, 0, 0.44)" />
                    </div>
                )}

                {error && !isLoading && (
                    <div className="error-container">
                        <span className="error-message">
                            Could not read the sales channels from Shopify. Please try again later.
                        </span>
                    </div>
                )}

                {!isLoading && !error && channels.length === 0 && (
                    <p className="salesChannel__empty">
                        Shopify returned no sales channels for this store.
                    </p>
                )}

                {hasChannels && (
                    <div className="salesChannel__groups">
                        {groups.map((group) => (
                            <div className="salesChannel__group" key={group.title}>
                                <p className="salesChannel__groupTitle">{group.title}</p>
                                {group.channels.map((channel) => (
                                    <SalesChannelOption
                                        key={channel.publicationId}
                                        name={channel.name ?? channel.publicationId}
                                        checked={selectedIds.includes(channel.publicationId)}
                                        onToggle={() => handleToggle(channel.publicationId)}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
